import { useState } from "react";
import { Layers, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { EmptyState, fmt, fmtDate, fmtDateTime } from "@/components/shared";
import { motion, useReducedMotion } from "framer-motion";
import { CountUp, WorkingDaysBar } from "@/components/PlanVisuals";

const Stat = ({ label, value, testId, cls = "text-white" }) => (
  <div className="min-w-0"><p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p><p className={`font-mono text-sm ${cls} truncate`} data-testid={testId}>{value}</p></div>
);

export function StatusPill({ status }) {
  const active = status === "active";
  return <span data-testid="investment-status" className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${active ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-cyan-500/15 text-cyan-300 border-cyan-500/30"}`}>{active ? "ACTIVE" : "MATURED"}</span>;
}

function Ledger({ id }) {
  const [rows, setRows] = useState(null);
  if (rows === null) { api.get(`/investments/${id}/ledger`).then((r) => setRows(r.data.ledger)).catch(() => setRows([])); return <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-cyan-400" /></div>; }
  if (!rows.length) return <p className="text-xs text-slate-500 py-3" data-testid="ledger-empty">No ROI credited yet — first payout on the next working day.</p>;
  return (
    <div className="overflow-x-auto max-h-64 overflow-y-auto" data-testid="roi-ledger">
      <table className="w-full text-xs">
        <thead><tr className="text-left text-slate-500 border-b border-white/5 sticky top-0 bg-[#0A0F1D]"><th className="py-2 pr-3 font-medium">Working day</th><th className="py-2 pr-3 font-medium">Date</th><th className="py-2 pr-3 font-medium">ROI</th><th className="py-2 pr-3 font-medium">Cumulative</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className="border-b border-white/5 last:border-0" data-testid={`ledger-row-${r.day_index}`}>
            <td className="py-1.5 pr-3 font-mono text-slate-300">{r.day_index} / {r.total_days}</td><td className="py-1.5 pr-3 text-slate-400">{r.roi_date} · {r.weekday}</td>
            <td className="py-1.5 pr-3 font-mono text-emerald-300">+{fmt(r.amount)}</td><td className="py-1.5 pr-3 font-mono text-slate-200">{fmt(r.cumulative_roi)}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

function InvestmentCard({ i, index = 0 }) {
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();
  const active = i.status === "active";
  return (
    <motion.div initial={reduce ? false : { opacity: 0, y: 20, scale: 0.98 }} whileInView={{ opacity: 1, y: 0, scale: 1 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: Math.min(index * 0.1, 0.5) }}
      className="rounded-2xl border border-white/5 bg-slate-900/40 p-5 transition-[border-color,box-shadow] duration-500 hover:border-cyan-500/30 hover:shadow-[0_12px_40px_-16px_rgba(0,200,255,0.3)]" data-testid={`investment-row-${i.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-heading text-white">{i.plan_name}</p>
          <p className="text-xs text-slate-500">{i.source === "deposit" ? "Auto from deposit" : "Manual"} · Started {fmtDate(i.started_at)}</p>
        </div>
        <StatusPill status={i.status} />
      </div>
      <div className="mt-4">
        <WorkingDaysBar done={i.days_credited} total={i.duration_days} testId="inv-progress-bar" />
        <p className="sr-only" data-testid="inv-progress">{i.days_credited} / {i.duration_days} working days</p>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        <Stat label="Invested" value={`${fmt(i.amount)} USDT`} testId="inv-amount" />
        <Stat label="Daily ROI" value={`${i.daily_percentage}% · ${fmt(i.daily_roi)} USDT`} testId="inv-daily-roi" cls="text-cyan-300" />
        <Stat label="ROI Earned" value={<><CountUp value={i.roi_earned} decimals={2} /> USDT</>} testId="inv-roi-earned" cls="text-emerald-300" />
        <Stat label="Remaining ROI" value={`${fmt(i.remaining_roi)} USDT`} testId="inv-remaining-roi" />
        <Stat label="Current Working Day" value={active ? `${i.current_day} / ${i.duration_days}` : "—"} testId="inv-current-day" />
        <Stat label="Completed Days" value={<CountUp value={i.days_credited} />} testId="inv-completed-days" />
        <Stat label="Remaining Days" value={<CountUp value={i.remaining_days} />} testId="inv-remaining-days" />
        <Stat label="Next ROI Date" value={active ? fmtDateTime(i.next_credit_at) : "—"} testId="inv-next-roi" />
        <Stat label="Maturity Date" value={i.maturity_date ? fmtDate(i.maturity_date) : "—"} testId="inv-maturity" />
        <Stat label="Max Total ROI" value={`${fmt(i.max_roi)} USDT`} testId="inv-max-roi" />
      </div>
      <button onClick={() => setOpen(!open)} data-testid="inv-ledger-toggle" className="mt-4 text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} {open ? "Hide" : "View"} ROI ledger
      </button>
      {open && <div className="mt-2"><Ledger id={i.id} /></div>}
    </motion.div>
  );
}

export function InvestmentsList({ items }) {
  return (
    <div className="glass rounded-2xl p-6" data-testid="my-investments">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading text-lg text-white">My Investments</h3>
        <span className="text-xs text-slate-500">{items.filter((i) => i.status === "active").length} active · {items.filter((i) => i.status !== "active").length} matured</span>
      </div>
      {items.length === 0 ? (
        <EmptyState icon={Layers} title="No investments yet" subtitle="Approved deposits are automatically placed into the 200 Working Days Plan and earn 1% of principal every working day (Mon–Fri). You can also invest from your available balance above." />
      ) : <div className="space-y-4">{items.map((i, idx) => <InvestmentCard key={i.id} i={i} index={idx} />)}</div>}
    </div>
  );
}
